import { Editor } from 'codemirror';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { CodeMirrorMark, MarksHelper } from '../code-mirror-wrapper/marks-helper';
import { CodemirrorContext } from '../codemirror-context';
import { HunspellService } from './hunspell.service';

export const LAST_SET_SPELLCHECKER_LANG_LS_KEY = 'last-set-spellchecker-lang';

/**
 * A few callbacks to decouple the spellchecker from its calling component
 * (so that we can use it both for the angularjs directive (codemirror-and-toolbar)
 *  and the angular component (single-message-editor))
 */
export interface SpellCheckerContext {
  getLastSetSpellcheckerLang(): string;

  saveLastSetSpellcheckerLang(lang: string): void;

  onSpellSuggestionsFromOutsideOfAngular(suggestions);

  /**
   * updates the spell suggestions in codemirror-and-toolbar's scope, so that they are reflected in the context menu
   * (which will open very soon)
   */
  onSpellSuggestions(suggestions): void;
}

export class SupportedLang {
  constructor(
    public code: string,
    public label: string,
    public cssClass: string,
    public editorDirection: 'ltr' | 'rtl'
  ) {}
}

export class CodemirrorSpellChecker {
  public supportedLanguages = {
    fr: new SupportedLang('fr', 'Français', 'flag-FR', 'ltr'),
    en: new SupportedLang('en', 'English', 'flag-GB', 'ltr'),
    de: new SupportedLang('de', 'Deutsch', 'flag-DE', 'ltr'),
    es: new SupportedLang('es', 'Español', 'flag-ES', 'ltr'),
    it: new SupportedLang('it', 'Italiano', 'flag-IT', 'ltr'),
    ar: new SupportedLang('ar', 'العربية', 'flag-arab-league', 'rtl'),
    pl: new SupportedLang('pl', 'Polski', 'flag-PL', 'ltr'),
  };

  public currentLanguage: SupportedLang;

  private spellCheckRequest$ = new Subject<void>();
  private marks: CodeMirrorMark[] = [];
  private lastClickedMark: CodeMirrorMark;
  private hunspell;

  // references to an event handler, so that we can unregister it on destroy
  private changeEventHandler: () => void;

  // The following regex includes the \s class (space, tab, and other unicode spaces),
  // and all the special chars I could find on my keyboard,
  // except for '-' which can be part of words in some languages.
  // At the moment, we don't spellcheck languages with fancy characters,
  // so the regex excludes everything above \U+1FFF
  // (this is a quite arbitrary limit, but it conveniently excludes u+200D (zero-width joiner) which is present in many emojis )
  // Some ponctuation signs used in arabic are also present (arabic question mark \u061F for the moment)

  // Note that as javascript is USC-2 encoded, the characters above \U+FFFF are excluded by excluding the surrogate pairs ranges,
  // from U+D800 to U+DFFF (cf https://mathiasbynens.be/notes/javascript-encoding )

  // Note that the regex is wrapped in capture parenthesis so that separators are included in the split() result,
  // cf https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/split#Splitting_with_a_RegExp_to_include_parts_of_the_separator_in_the_result

  private wordSeparatorRE = /([\s~`!@#$%^&*()_+={}[\]|\\;:'"<>,.?/\u061F\u2000-\uFFFF]+)/;

  constructor(
    private codeMirror: Editor,
    private hunspellService: HunspellService,
    private context: SpellCheckerContext,
    private codemirrorContext: CodemirrorContext
  ) {
    this.spellCheckRequest$.pipe(debounceTime(500)).subscribe(() => this.doCheckSpell());

    this.changeEventHandler = () => this.debouncedCheckSpell();
    this.codeMirror.on('change', this.changeEventHandler);
  }

  cleanUp(): void {
    this.codeMirror.off('change', this.changeEventHandler);
    this.deleteHunspell();
  }

  // called by codemirror-wrapper.component
  suggestionsForClickedMarks(marks: CodeMirrorMark[]) {
    const mark = marks.find(m => this.marks.includes(m));
    if (mark) {
      this.lastClickedMark = mark;
      const word = this.codeMirror.getDoc().getRange(mark.find().from, mark.find().to);
      return this.hunspellSuggestions(word);
    } else {
      return null;
    }
  }

  // called outside of angularjs, by codemirrorAndToolbarDirective
  onLeftMouseButtonDown(marksAtClickPosition: CodeMirrorMark[]) {
    const mark = marksAtClickPosition.find(m => this.marks.includes(m));
    if (mark) {
      this.lastClickedMark = mark;
      const word = this.codeMirror.getDoc().getRange(mark.find().from, mark.find().to);
      this.context.onSpellSuggestionsFromOutsideOfAngular(this.hunspellSuggestions(word));
    } else {
      this.context.onSpellSuggestionsFromOutsideOfAngular(null);
    }
  }

  // called via ng-click in the context menu
  replacementClicked(replacement: string) {
    if (replacement) {
      this.marks = this.marks.filter(iteredMark => iteredMark !== this.lastClickedMark);
      const fromTo = this.lastClickedMark.find();
      if (fromTo) {
        // fromTo is sometimes undefined,
        // for example if the user types on the keyboard between their click and material opening the popup menu
        this.codeMirror.getDoc().replaceRange(replacement, fromTo.from, fromTo.to);
      }
      this.lastClickedMark.clear();
      this.context.onSpellSuggestions(null);
    }
  }

  /**
   * Rebuilds the underlying hunspell spell checker for the given language,
   * or the site default if the given language is not supported,
   * or french if the site default is not supported;
   * then triggers a spell check.
   */
  setLanguageCode(code) {
    let bestEffortLangCode = 'fr';
    const lastSetlangCode = this.context.getLastSetSpellcheckerLang();
    if (this.isLanguageSupported(code)) {
      bestEffortLangCode = code;
    } else if (this.isLanguageSupported(lastSetlangCode)) {
      bestEffortLangCode = lastSetlangCode;
    }
    this.currentLanguage = this.supportedLanguages[bestEffortLangCode];
    this.context.saveLastSetSpellcheckerLang(bestEffortLangCode); // save it to localstorage
    if (this.codemirrorContext) {
      this.codemirrorContext.languageCode = bestEffortLangCode; // make it available for favorite suggestions
    }
    this.hunspellService.newHunspell(bestEffortLangCode).subscribe(hunspell => {
      this.setHunspell(hunspell);
      this.debouncedCheckSpell();
    });
    this.codeMirror.setOption('direction', this.currentLanguage.editorDirection);
  }

  private debouncedCheckSpell(): void {
    this.spellCheckRequest$.next();
  }

  private isLanguageSupported(code: string): boolean {
    return !!this.supportedLanguages[code];
  }

  private setHunspell(hunspell) {
    this.deleteHunspell();
    this.hunspell = hunspell;
  }

  private deleteHunspell() {
    if (this.hunspell) {
      this.hunspell.delete();
    }
  }

  private isHunspellValid(word) {
    let valid = true;
    if (this.hunspell) {
      try {
        valid = this.hunspell.spell(word).valid;
      } catch (e) {
        console.warn('error during spell checking', e);
      }
    }
    return valid;
  }

  private hunspellSuggestions(word) {
    try {
      return this.hunspell.suggest(word);
    } catch (e) {
      console.warn('error while computing spell suggestions', e);
      return null;
    }
  }

  /**
   * Splits the content of the editor into words, using an arbitrary character class as separators.
   * Returned words have the following properties: {
   *   from: lineCh,
   *   to:   lineCh,
   *   text: string
   * }
   */
  private getWordsFromEditor() {
    const words = [];
    this.codeMirror.getDoc().eachLine(line => {
      // .eachLine() doesn't seem to work if I write this anonymous function without a {} block.
      words.push(...this.getWordsFromLine(line));
    });
    return words;
  }

  private getWordsFromLine(line) {
    const words = [];
    const wordsAndSeparators = line.text.split(this.wordSeparatorRE);
    let offsetInLine = 0;
    wordsAndSeparators.forEach((wordOrSeparator: string) => {
      if (this.isCheckableWord(wordOrSeparator)) {
        words.push(this.newWord(line.lineNo(), offsetInLine, offsetInLine + wordOrSeparator.length, wordOrSeparator));
      }
      offsetInLine += wordOrSeparator.length;
    });
    return words;
  }

  private isCheckableWord(wordOrSeparator) {
    return wordOrSeparator.length > 0 && wordOrSeparator.length < 30 && !wordOrSeparator.match(this.wordSeparatorRE);
  }

  private newWord(lineNo, fromOffsetInLine, toOffsetInLine, text) {
    return {
      from: this.newLineCh(lineNo, fromOffsetInLine),
      to: this.newLineCh(lineNo, toOffsetInLine),
      text,
    };
  }

  private newLineCh(line, ch) {
    return { line, ch };
  }

  /**
   * Entry point to links detection, called on every document change but with a 0.5s debounce
   */
  private doCheckSpell() {
    this.marks.forEach(mark => mark.clear());
    this.marks = [];
    const words = this.getWordsFromEditor();
    words.forEach(word => {
      const marksAtThisWord = this.codeMirror.getDoc().findMarks(word.from, word.to);
      if (marksAtThisWord.length === 0 && !this.isHunspellValid(word.text)) {
        this.marks.push(MarksHelper.createBadSpellingMark(this.codeMirror, word.from, word.to));
      }
    });
  }
}
